import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import ScanLoader from "@/components/ScanLoader";
import RenderBudgetMeter from "@/components/RenderBudgetMeter";
import {
  cancelVideoRender,
  checkAutoVideoChannelKitNames,
  getAutoVideo,
  generateThumbnail,
  getBackendVersion,
  getScenePrompt,
  insertSubjectIntoVideo,
  listVideoProviders,
  listImageProviders,
  refreshAutoVideoChannelKitNames,
  regenerateAutoVideoChannelKit,
  regenerateVoiceover,
  renderAutoVideo,
  replaceSceneImage,
  restoreAutoVideoChannelKitImage,
  restoreRenderVersion,
  revertSceneImage,
  saveScenePrompt,
  resetScenePrompt,
  approveScene,
  unapproveScene,
  regenerateScene,
  getSceneApprovalState,
  generateAllScenes,
  animateScene,
  stitchAnimatedScenes,
  setSceneAnimationPrompt,
} from "@/lib/api";
import SaveKitButton from "@/components/SaveKitButton";
import ScriptEditor from "@/components/ScriptEditor";
import SceneApprovalList from "@/components/SceneApprovalList";
import SceneCompositionCard from "@/components/SceneCompositionCard";
import { BannerSafeZonePreview } from "@/components/BannerSafeZonePreview";
import SubjectInsertCard from "@/components/SubjectInsertCard";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Append a stable cache-buster query param to an asset URL so the browser
 * always loads the freshest copy after a re-render. Stable means: the
 * URL only changes when the underlying asset changes (we use a timestamp
 * the backend sets on completion). Without this, retrying a render keeps
 * showing the OLD MP4 / thumbnail because the URL is identical.
 */
function withCacheBust(url, key) {
  if (!url) return url;
  if (!key) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}v=${encodeURIComponent(key)}`;
}

// Compact colored pill for the AutoVideo Channel Kit "Name options"
// list. Mirrors `NameAvailabilityBadge` from the Create Channel wizard
// but stays small enough to fit inline with each name row.
function KitNameAvailabilityBadge({ availability }) {
  if (!availability) {
    return (
      <span
        data-testid="av-kit-name-availability-unknown"
        title="Click ↻ check availability above to query YouTube"
        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[1px] rounded-sm border border-zinc-700 text-zinc-500"
      >
        ?
      </span>
    );
  }
  if (availability.exact_match) {
    return (
      <span
        data-testid="av-kit-name-availability-taken"
        title="An existing YouTube channel has this exact name — you'll compete with their brand."
        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[1px] rounded-sm border border-red-500/50 text-red-300 bg-red-500/10"
      >
        ✗ taken
      </span>
    );
  }
  if (availability.match_count >= 5) {
    return (
      <span
        data-testid="av-kit-name-availability-crowded"
        title={`${availability.match_count} channels on YouTube look similar — no exact match but you'll compete for search visibility.`}
        className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[1px] rounded-sm border border-yellow-500/50 text-yellow-300 bg-yellow-500/10"
      >
        ⚠ crowded
      </span>
    );
  }
  return (
    <span
      data-testid="av-kit-name-availability-clear"
      title={`No exact match. ${availability.match_count} loosely similar channels found on YouTube.`}
      className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-[1px] rounded-sm border border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
    >
      ✓ clear
    </span>
  );
}


// Cross-origin <a download> is ignored by browsers — fetch as blob and
// trigger a same-origin download via createObjectURL. Used for the
// channel-kit per-image download buttons below.
async function downloadKitImage(url, filename) {
  try {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) throw new Error(`download failed: ${r.status}`);
    const blob = await r.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    toast.error("Couldn't download image");
  }
}

const STAGE_LABELS = {
  queued: "Queued",
  channel_kit: "Generating channel kit",
  script: "Writing narration script (Claude)",
  voiceover: "Recording voiceover (ElevenLabs)",
  done: "Complete",
};

// Plain-English stage labels + "what this means" subtitle.
// Keep these readable by non-engineers — no fal/ffmpeg jargon.
const STAGE_INFO = {
  queued: {
    label: "Getting started",
    sub: "Spinning up the render servers",
  },
  regenerating_voiceover: {
    label: "Re-recording your voiceover (in parallel)",
    sub: "The MP3 was wiped by the last deploy — regenerating it via ElevenLabs while scene + video gen run alongside",
  },
  waiting_for_voiceover: {
    label: "Waiting for voiceover to finish",
    sub: "Scenes are ready — just waiting on the ElevenLabs MP3 before stitching",
  },
  probing_audio: {
    label: "Step 1 of 4 · Measuring your voiceover",
    sub: "Figuring out how long the final video needs to be",
  },
  scene_images: {
    label: "Step 2 of 4 · Designing the scene images",
    sub: "AI is painting one still image for each scene of your script (~30s)",
  },
  ltx_video: {
    label: "Step 3 of 4 · Animating each scene into video",
    sub: "AI is turning each still into a moving clip with real motion",
  },
  // Legacy old-backend stage — kept so the label still reads cleanly when
  // production hasn't redeployed yet.
  encoding_clips: {
    label: "Step 3 of 4 · Building each scene clip",
    sub: "Encoding each scene as a 720p video segment",
  },
  muxing: {
    label: "Step 4 of 4 · Stitching the final video together",
    sub: "Adding your voiceover, concatenating clips, packaging as MP4",
  },
  done: { label: "Done!", sub: "Your video is ready to play, download, or upload." },
};

// Back-compat label-only map used in older string interpolations.
const VIDEO_STAGE_LABELS = Object.fromEntries(
  Object.entries(STAGE_INFO).map(([k, v]) => [k, v.label])
);

// Map a backend stage value (possibly with a "_N_of_M" suffix) to the
// stageKeys index used by ScanLoader, and produce a human label.
function resolveVideoStage(raw) {
  if (!raw) return { key: null, label: "—", progress: null };
  // Accept both new (scene_images / ltx_video) and legacy (encoding_clips)
  // suffixed stages.
  const m = raw.match(/^(scene_images|ltx_video|encoding_clips)_(\d+)_of_(\d+)$/);
  if (m) {
    const [, key, done, total] = m;
    const baseLabel = VIDEO_STAGE_LABELS[key] || key;
    return {
      key,
      label: `${baseLabel} (${done} of ${total})`,
      progress: { done: Number(done), total: Number(total) },
    };
  }
  return { key: raw, label: VIDEO_STAGE_LABELS[raw] || raw, progress: null };
}

function nFmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function dur(seconds) {
  if (!seconds) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

// Renders a single line from the image-gen live log. For lines that end in
// "attempting…" we attach a live elapsed-second ticker so the user can SEE
// that fal is actively working — not stuck. Done/OK/ERROR lines are static.
function LiveLogLine({ line, firstSeenMs, isStale }) {
  const isInFlight = line.endsWith("attempting…") || line.endsWith("attempting...");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!isInFlight) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isInFlight]);
  const elapsedSec = isInFlight && firstSeenMs ? Math.floor((Date.now() - firstSeenMs) / 1000) : 0;

  // Color: red ONLY for actual failures (ERROR/FAILED/TIMEOUT in the
  // line text); green for completions; yellow for active in-flight waits
  // (no matter how long they take — slow ≠ failed); faded grey for stale
  // attempts that have been superseded by a newer log line.
  const isHardFailure =
    line.includes("ERROR") || line.includes("FAILED") || line.includes("TIMEOUT");
  const cls = isHardFailure
    ? "text-red-300"
    : line.includes(" OK ") || line.includes(" OK in ")
    ? "text-emerald-300"
    : isInFlight && isStale
    ? "text-zinc-500"
    : isInFlight
    ? "text-yellow-300"
    : "";

  return (
    <div className={cls}>
      {line}
      {isInFlight && firstSeenMs && !isStale ? (
        <span className="ml-2 text-yellow-400">
          · {elapsedSec}s elapsed
          {elapsedSec > 60 ? " · fal GPU queue is slow today — render will auto-fall-back if needed" : " · fal GPU working"}
        </span>
      ) : null}
      {isInFlight && isStale ? (
        <span className="ml-2 text-zinc-600">· superseded (already completed)</span>
      ) : null}
      {/* Force a re-render each second while in flight */}
      {isInFlight && !isStale ? <span className="hidden">{tick}</span> : null}
    </div>
  );
}

export default function AutoVideo() {
  const { jobId } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [thumbing, setThumbing] = useState(false);
  // Reference-guided thumbnail inputs.
  const [thumbRefFile, setThumbRefFile] = useState(null);
  const [thumbRefPreview, setThumbRefPreview] = useState(null);
  const [thumbNotes, setThumbNotes] = useState("");
  const [showThumbGuide, setShowThumbGuide] = useState(false);
  const [regenVo, setRegenVo] = useState(false);
  // Render-history restore in flight (the version number that's restoring).
  const [restoringVersion, setRestoringVersion] = useState(null);
  // Diagnostics returned by the last force-fresh /render call so the user
  // gets visible proof that cache invalidation actually wiped stale assets.
  const [lastRenderDiag, setLastRenderDiag] = useState(null);
  const pollRef = useRef(null);
  // Track first time we saw each in-flight log line so the elapsed-second
  // counter is accurate even though the backend doesn't emit timestamps.
  // Map: log line text → ms epoch when first seen.
  const [inFlightFirstSeen, setInFlightFirstSeen] = useState({});
  // Track when the current video_stage first appeared so we can show a
  // "stage stalled for N seconds" hint without misleading the user with
  // a hard-coded "queued for 2 min" rule.
  const [stageStartedAt, setStageStartedAt] = useState(null);
  const [stageStallSec, setStageStallSec] = useState(0);
  const lastStageRef = useRef(null);
  // Auto-retry watermark — if backend marks the render failed and the
  // failure mentions "watchdog" or "stalled" or "timeout", auto-retry once
  // without user action. Tracked per session so a real persistent failure
  // still surfaces to the user.
  const autoRetriedRef = useRef(false);
  // One-shot guard so we don't toast "auto-restarted" on every poll.
  const autoRestartToastShownRef = useRef(false);
  // Monotonic request counter — protects against out-of-order poll
  // responses overwriting newer state with older state. Under load,
  // production responses can take >2s and overlap with the next poll;
  // without this gate the older response can land last and freeze the
  // UI on a stale "running" snapshot even though the render is already
  // complete server-side. We only ever apply a response if its seq is
  // strictly greater than the most-recently-applied seq.
  const requestSeqRef = useRef(0);
  const lastAppliedSeqRef = useRef(0);
  // Single in-flight guard — coalesces overlapping polls. Always reset
  // in the `finally` block so a thrown error never wedges the poller.
  const inFlightRef = useRef(false);
  // Tracks the current setInterval period so we can step it up/down
  // dynamically (2s while running, 8s once terminal) without leaking
  // intervals.
  const pollIntervalMsRef = useRef(2000);
  // Backend version (fetched once on mount). Used to display "deployment
  // up-to-date?" badge so user can verify the latest code is serving.
  const [backendVersion, setBackendVersion] = useState(null);
  // Scene approval state (Active-Channels two-step flow). Null until
  // the first poll returns it; { scenes_status, scene_image_urls,
  // narration_slices, scene_descriptions, all_scenes_approved, ... }.
  const [sceneApproval, setSceneApproval] = useState(null);
  // Set once Animate-all has fired off /render so we can show a spinner
  // on the button until video_status flips to "running".
  const [animateBusy, setAnimateBusy] = useState(false);
  // Available video providers + currently-selected one. Default to "veo3-direct"
  // (Google Veo 3, top quality) — overridden by /video-providers default if backend differs.
  const [videoProviders, setVideoProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("veo3-direct");
  // Available IMAGE providers (one of fal-flux / openai-gpt-image /
  // gemini-nano-banana-pro) and the currently-selected one for this job.
  const [imageProviders, setImageProviders] = useState([]);
  const [selectedImageProvider, setSelectedImageProvider] = useState("openai-gpt-image");
  // Tracks which scene tile (if any) is currently mid-upload so we
  // can show a spinner instead of the static thumbnail. Hidden file
  // inputs key off this index too. Null = idle.
  const [uploadingSceneIdx, setUploadingSceneIdx] = useState(null);
  const replaceInputRef = useRef(null);
  const pendingReplaceIdxRef = useRef(null);
  // Channel-kit regenerate is a slow call (~30-90s) — disable the
  // button while it runs to prevent double-clicks burning credits.
  const [kitRegenerating, setKitRegenerating] = useState(false);
  const [checkingNames, setCheckingNames] = useState(false);
  const [refreshingNames, setRefreshingNames] = useState(false);
  // Which scene index is currently open in the AI-prompt editor modal,
  // or null when closed. Set by the per-scene `✎ PROMPT` button.
  const [promptEditorIdx, setPromptEditorIdx] = useState(null);
  // The pipeline version this frontend was built against. Bump in lockstep
  // with backend BACKEND_PIPELINE_VERSION when shipping breaking changes.
  const EXPECTED_PIPELINE_VERSION = "v11-cdn-cache-fix-veo3-2026-06-13";

  useEffect(() => {
    getBackendVersion()
      .then(setBackendVersion)
      .catch(() => {/* non-critical */});
    listVideoProviders()
      .then((d) => {
        setVideoProviders(d?.providers || []);
        if (d?.default) setSelectedProvider(d.default);
      })
      .catch(() => {/* non-critical — falls back to LTX default */});
    listImageProviders()
      .then((d) => {
        setImageProviders(d?.providers || []);
        if (d?.default) setSelectedImageProvider(d.default);
      })
      .catch((err) => {
        // Stale frontend bundle (Cloudflare-cached pre-feature build) or
        // backend unreachable. Hardcode Nano Banana Pro as the fallback
        // so the picker never stays stuck at "Loading…" forever — it's
        // also the new default for accurate object counts.
        // eslint-disable-next-line no-console
        console.warn("listImageProviders failed, using fallback:", err);
        setImageProviders([
          {
            id: "gemini-nano-banana-pro",
            label: "Gemini Nano Banana Pro (default)",
            cost_per_image_usd: 0.030,
            expected_seconds_per_image: 18,
          },
        ]);
      });
  }, []);

  const load = useCallback(async () => {
    // Skip if a previous poll is still in flight. Without this, slow
    // production responses cause concurrent overlapping polls that
    // resolve out-of-order and clobber state.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    // Tag this request with a monotonic seq so we can drop stale replies
    // that resolved out-of-order (poll N+1 finishes before poll N).
    requestSeqRef.current += 1;
    const mySeq = requestSeqRef.current;
    try {
      const d = await getAutoVideo(jobId);
      // STALE GUARD: if a later request has already applied its result
      // since we kicked off, discard ours. Without this, a slow response
      // can land last and freeze the UI on an old "running" snapshot.
      if (mySeq <= lastAppliedSeqRef.current) {
        return;
      }
      lastAppliedSeqRef.current = mySeq;
      // Inform user when the backend auto-cleaned a stale render on us.
      if (d.auto_restarted && !autoRestartToastShownRef.current) {
        autoRestartToastShownRef.current = true;
        toast.info("Previous render was abandoned — auto-restarted for you.");
      }
      setDoc(d);
      // Active-Channels two-step pipeline: when the job uses the
      // per-scene approval flow, fetch its dedicated state on the same
      // tick. Cheap projection (no kit / large arrays), gracefully
      // skipped for legacy jobs that never initialized scenes_status.
      const usesApproval = Boolean(d?.source_active_channel_id);
      if (usesApproval) {
        try {
          const s = await getSceneApprovalState(jobId);
          if (mySeq >= lastAppliedSeqRef.current && Array.isArray(s?.scenes_status)) {
            setSceneApproval(s);
          }
        } catch (sErr) {
          // Endpoint might 404 transiently right after job create —
          // ignore and try again next tick.
          console.debug("approval-state poll skipped:", sErr?.message);
        }
      } else {
        // Not an active-channel job — ensure approval state is cleared.
        // setState(null) is a no-op when already null.
        setSceneApproval(null);
      }
      // Track when the video_stage changed so we can show stalled-time.
      const newStage = d.video_stage || null;
      if (newStage && newStage !== lastStageRef.current) {
        lastStageRef.current = newStage;
        setStageStartedAt(Date.now());
        setStageStallSec(0);
      }
      // Update first-seen timestamps for "…attempting" log lines.
      if (Array.isArray(d.image_log)) {
        setInFlightFirstSeen((prev) => {
          const next = { ...prev };
          const now = Date.now();
          for (const line of d.image_log) {
            if (
              (line.endsWith("attempting…") || line.endsWith("attempting..."))
              && !(line in next)
            ) {
              next[line] = now;
            }
          }
          return next;
        });
      }
      const pipelineDone = d.status !== "running";
      const videoActuallyDone =
        d.video_status === "complete" || d.video_status === "failed";
      // Don't stop polling just because no video render was ever started
      // (`!d.video_status`) — the user may click "▸ Render full video"
      // any moment and we need to be alive to catch the transition. Same
      // applies to subsequent re-renders: a "complete" video can flip
      // back to "running" when the user clicks Re-render.
      //
      // Strategy: keep polling alive forever while the component is
      // mounted, but throttle to 8s when we're in a terminal state to
      // avoid spamming the backend after a successful render.
      const isTerminal = pipelineDone && videoActuallyDone;
      const desiredInterval = isTerminal ? 8000 : 2000;
      if (desiredInterval !== pollIntervalMsRef.current) {
        pollIntervalMsRef.current = desiredInterval;
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(load, desiredInterval);
      }
      if (isTerminal) {
        if (d.status === "failed") toast.error(d.error || "Pipeline failed");
        else if (d.status === "complete" && !d._notified) {
          toast.success("Auto-video ready");
          d._notified = true;
        }
        if (d.video_status === "failed") {
          // Auto-retry once if the failure looks transient (watchdog stall,
          // LTX timeout, ffmpeg hang). Don't auto-retry real config errors.
          const err = (d.video_error || "").toLowerCase();
          const looksTransient = err.includes("watchdog")
            || err.includes("stalled")
            || err.includes("timeout")
            || err.includes("hung")
            || err.includes("ltx video gen failed");
          if (looksTransient && !autoRetriedRef.current) {
            autoRetriedRef.current = true;
            toast.info("Render hit a transient timeout — auto-retrying once…");
            try {
              await renderAutoVideo(jobId);
              await load();
            } catch (rerr) {
              toast.error(rerr?.response?.data?.detail || "Auto-retry failed");
            }
            return;
          }
          toast.error(d.video_error || "Video render failed");
        }
      }
    } catch (err) {
      // BUG FIX: do NOT clearInterval here. A single transient error
      // (network blip, brief 502 during a deploy, auth re-check) would
      // permanently kill the polling loop and freeze the UI on the
      // "running" view — even after the render completes server-side.
      // Just log and let the next poll tick recover.
      console.warn("auto-video poll failed (will retry):", err?.message || err);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [jobId]);

  // Replace one scene image with a user upload. Triggered by the
  // hidden file input that lives once at the bottom of the section
  // (pendingReplaceIdxRef tells it which scene to target). Letterboxes
  // are handled server-side so the client just hands over raw bytes.
  const handleSceneFileChosen = useCallback(async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";  // allow re-picking the same file
    const idx = pendingReplaceIdxRef.current;
    pendingReplaceIdxRef.current = null;
    if (!f || idx === null || idx === undefined) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Image too large (50 MB max)");
      return;
    }
    setUploadingSceneIdx(idx);
    const toastId = toast.loading(`Uploading scene ${idx + 1}…`);
    try {
      const resp = await replaceSceneImage({ jobId, sceneIdx: idx, file: f });
      toast.success(
        `Scene ${idx + 1} replaced · letterboxed from ${resp.letterboxed_from.width}×${resp.letterboxed_from.height}`,
        { id: toastId, duration: 4000 },
      );
      // Refresh the doc so the new image + user_replaced_scenes flag
      // pop up in the UI immediately.
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Upload failed",
        { id: toastId },
      );
    } finally {
      setUploadingSceneIdx(null);
    }
  }, [jobId, load]);

  // Drop the user-replaced flag for one scene so the next render
  // regenerates a fresh AI image for it.
  const handleSceneRevert = useCallback(async (idx) => {
    try {
      await revertSceneImage({ jobId, sceneIdx: idx });
      toast.success(`Scene ${idx + 1} will regenerate on next render`, { duration: 3500 });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revert failed");
    }
  }, [jobId, load]);

  // Re-roll the channel-kit banners + avatar. The endpoint feeds the
  // original channel + playbook back through `generate_channel_kit`,
  // so the new banners benefit from any prompt / spec changes the
  // backend has shipped since the original render (e.g. the
  // 2560×1440 conform + strict-no-text rules). Script, voiceover,
  // scenes, and the rendered video are LEFT ALONE.
  //
  // When `target` is passed (e.g. "banner_1.png"), only that single
  // image gets re-rolled — uses the existing kit's prompts so the
  // new image stays stylistically consistent.
  const handleRegenerateChannelKit = useCallback(async (target = null) => {
    if (kitRegenerating) return;
    setKitRegenerating(target || "all");
    const label = target ? target.replace(".png", "").replace("_", " ") : "banners + avatar";
    const toastId = toast.loading(
      `Regenerating ${label} (~30-90s)…`,
    );
    try {
      await regenerateAutoVideoChannelKit(jobId, target ? { target } : undefined);
      toast.success(
        target ? `${label} regenerated · new image ready` : "Channel kit regenerated · new banners ready",
        { id: toastId, duration: 4500 },
      );
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Channel kit regenerate failed",
        { id: toastId },
      );
    } finally {
      setKitRegenerating(false);
    }
  }, [jobId, kitRegenerating, load]);

  // Restore an archived banner version back into the active slot.
  // The currently-active image becomes the new archive, so the user
  // can flip between concepts as many times as they like without ever
  // losing one. Triggered by clicking a thumb in the history strip
  // under each banner.
  const handleRestoreKitImage = useCallback(async (target, archive_filename) => {
    if (kitRegenerating) return;
    setKitRegenerating(`restore-${target}`);
    const toastId = toast.loading("Restoring banner version…");
    try {
      await restoreAutoVideoChannelKitImage(jobId, { target, archive_filename });
      toast.success("Banner restored from history", {
        id: toastId, duration: 3500,
      });
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Restore failed",
        { id: toastId },
      );
    } finally {
      setKitRegenerating(false);
    }
  }, [jobId, kitRegenerating, load]);

  // Backfill YouTube availability badges for the kit's name options.
  // Used when a saved kit predates the availability-check feature, or
  // when the user wants a fresh re-check. Toast-driven; updates the
  // kit doc in-place when complete.
  const handleCheckNames = useCallback(async () => {
    if (checkingNames) return;
    setCheckingNames(true);
    const toastId = toast.loading("Checking names against YouTube…");
    try {
      await checkAutoVideoChannelKitNames(jobId);
      toast.success("Availability badges added", {
        id: toastId, duration: 3500,
      });
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Availability check failed",
        { id: toastId },
      );
    } finally {
      setCheckingNames(false);
    }
  }, [jobId, checkingNames, load]);

  // Generate a fresh batch of channel-name suggestions, excluding any
  // names already shown. Replaces the current list with 5 brand-new
  // ones — availability badges attached automatically.
  const handleRefreshNames = useCallback(async () => {
    if (refreshingNames) return;
    setRefreshingNames(true);
    const toastId = toast.loading("Generating fresh name ideas (~10-20s)…");
    try {
      await refreshAutoVideoChannelKitNames(jobId);
      toast.success("Fresh names ready", {
        id: toastId, duration: 3500,
      });
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Refresh names failed",
        { id: toastId },
      );
    } finally {
      setRefreshingNames(false);
    }
  }, [jobId, refreshingNames, load]);

  useEffect(() => {
    // Reset interval period on every job (re-)mount — the load()
    // function below will adjust it dynamically based on terminal state.
    pollIntervalMsRef.current = 2000;
    load();
    pollRef.current = setInterval(load, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Update the per-stage stalled-second counter every second while a stage
  // is active. Resets when the stage changes (handled in load()).
  useEffect(() => {
    if (!stageStartedAt) return undefined;
    const id = setInterval(() => {
      setStageStallSec(Math.floor((Date.now() - stageStartedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [stageStartedAt]);

  const status = doc?.status;
  const stage = doc?.stage;
  const result = doc?.result;
  const kit = result?.channel_kit;
  const script = result?.script;
  const voiceover = result?.voiceover;
  const idea = result?.idea;
  const channel = result?.channel;
  const videoStatus = doc?.video_status;
  const videoStage = doc?.video_stage;
  const video = doc?.video;
  const videoErr = doc?.video_error;
  const thumbnail = doc?.thumbnail;

  const triggerThumbnail = async (opts = {}) => {
    setThumbing(true);
    try {
      await generateThumbnail(jobId, opts);
      toast.success(
        opts.referenceFile || opts.notes
          ? "Thumbnail re-rendered with your guidance"
          : "Thumbnail ready",
      );
      // Clear the guidance inputs after a successful guided run so the
      // user can iterate without re-uploading the same reference by
      // accident on the next click.
      if (opts.referenceFile || opts.notes) {
        setThumbRefFile(null);
        setThumbRefPreview(null);
        // Keep the notes — they're often reused across iterations.
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't generate thumbnail");
    } finally {
      setThumbing(false);
    }
  };

  const onPickThumbRef = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image file (PNG / JPG / WEBP)");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Reference image too large (10 MB max)");
      return;
    }
    setThumbRefFile(f);
    setThumbRefPreview(URL.createObjectURL(f));
  };

  const triggerVoRegen = async () => {
    setRegenVo(true);
    try {
      await regenerateVoiceover(jobId);
      toast.success("Voiceover regenerated");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate voiceover");
    } finally {
      setRegenVo(false);
    }
  };

  const cancelRender = async () => {
    try {
      await cancelVideoRender(jobId);
      toast.success("Render cancelled");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't cancel");
    }
  };

  const triggerRender = async (opts = {}) => {
    setRendering(true);
    try {
      const resp = await renderAutoVideo(jobId, {
        videoProvider: selectedProvider,
        imageProvider: selectedImageProvider,
        force: !!opts.force,
      });
      const lbl = videoProviders.find((p) => p.id === selectedProvider)?.label || selectedProvider;
      // Stash diagnostics so the Render Diagnostics panel can show
      // exactly how many cached clips/scenes/files got wiped this run.
      if (opts.force) {
        setLastRenderDiag({
          at: Date.now(),
          provider: lbl,
          invalidated: resp?.scenes_invalidated || { clips: 0, scenes: 0, work_files: 0 },
        });
      }
      toast.success(
        opts.force
          ? `Fresh render started — clearing cached scenes (${lbl})`
          : `Render started with ${lbl}`,
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start render");
    } finally {
      setRendering(false);
    }
  };

  // ---------- Scene approval handlers (Active-Channels two-step flow) ----------

  const refreshApproval = useCallback(async () => {
    try {
      const s = await getSceneApprovalState(jobId);
      if (Array.isArray(s?.scenes_status)) setSceneApproval(s);
    } catch (err) {
      console.debug("approval refresh skipped:", err?.message);
    }
  }, [jobId]);

  const handleApproveScene = async (idx) => {
    await approveScene(jobId, idx);
    await refreshApproval();
  };
  const handleUnapproveScene = async (idx) => {
    await unapproveScene(jobId, idx);
    await refreshApproval();
  };
  const handleRegenerateScene = async (idx, prompt) => {
    const tid = toast.loading(`Regenerating scene ${idx + 1}…`);
    try {
      const r = await regenerateScene(jobId, idx, prompt);
      if (r?.ok) toast.success(`Scene ${idx + 1} regenerated`, { id: tid });
      else toast.error(r?.error || "Regenerate failed", { id: tid });
      await refreshApproval();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Regenerate failed",
        { id: tid },
      );
    }
  };
  const handleUploadSceneImage = async (idx, file) => {
    const tid = toast.loading(`Uploading scene ${idx + 1}…`);
    try {
      await replaceSceneImage(jobId, idx, file);
      toast.success(`Scene ${idx + 1} replaced & approved`, { id: tid });
      await refreshApproval();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Upload failed",
        { id: tid },
      );
    }
  };
  const handleAnimateAll = async () => {
    setAnimateBusy(true);
    try {
      await renderAutoVideo(jobId, {
        videoProvider: selectedProvider,
        imageProvider: selectedImageProvider,
      });
      toast.success("Animation pipeline started");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start animation");
    } finally {
      setAnimateBusy(false);
    }
  };
  const handleRetryAllScenes = async () => {
    try {
      await generateAllScenes(jobId);
      toast.success("Retrying failed scenes…");
      await refreshApproval();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
    }
  };

  // Per-scene Veo3 8-second animation. Fires the backend job, then the
  // existing approval-state poll picks up the status transitions
  // (animating → ready / failed) and re-renders the card.
  const [animatingScenes, setAnimatingScenes] = useState(() => new Set());
  const [animateAllBusy, setAnimateAllBusy] = useState(false);
  const animateAllAbortRef = useRef(false);

  const markAnimating = (idx, on) => {
    setAnimatingScenes((prev) => {
      const next = new Set(prev);
      if (on) next.add(idx);
      else next.delete(idx);
      return next;
    });
  };

  const waitForSceneAnimation = useCallback(
    async (idx, { maxMs = 6 * 60 * 1000, intervalMs = 5000 } = {}) => {
      const deadline = Date.now() + maxMs;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, intervalMs));
        let snap;
        try {
          snap = await getSceneApprovalState(jobId);
        } catch {
          continue;
        }
        if (Array.isArray(snap?.scenes_status)) setSceneApproval(snap);
        const st = snap?.scene_animation_status?.[idx] || "";
        if (st === "ready") return { ok: true };
        if (st === "failed") {
          return {
            ok: false,
            error: snap?.scene_animation_errors?.[idx] || "Veo3 failed",
          };
        }
      }
      return { ok: false, error: "Timed out waiting for Veo3" };
    },
    [jobId],
  );

  const handleAnimateScene = async (idx) => {
    markAnimating(idx, true);
    const tid = toast.loading(`Sending scene ${idx + 1} to Veo3…`);
    try {
      await animateScene(jobId, idx);
      toast.loading(`Scene ${idx + 1} animating (8s)…`, { id: tid });
      await refreshApproval();
      const r = await waitForSceneAnimation(idx);
      if (r.ok) toast.success(`Scene ${idx + 1} animated`, { id: tid });
      else toast.error(`Scene ${idx + 1}: ${r.error}`, { id: tid });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || `Couldn't animate scene ${idx + 1}`,
        { id: tid },
      );
    } finally {
      markAnimating(idx, false);
    }
  };

  // "Animate All Approved": sequentially send each approved scene to
  // Veo3, waiting for one to finish before starting the next.
  const handleAnimateAllApproved = async () => {
    const statuses = sceneApproval?.scenes_status || [];
    const animStatus = sceneApproval?.scene_animation_status || [];
    const queue = [];
    for (let i = 0; i < statuses.length; i += 1) {
      if (
        statuses[i] === "approved"
        && animStatus[i] !== "ready"
        && animStatus[i] !== "animating"
      ) {
        queue.push(i);
      }
    }
    if (queue.length === 0) {
      toast.info("No approved scenes left to animate");
      return;
    }
    setAnimateAllBusy(true);
    animateAllAbortRef.current = false;
    let okCount = 0;
    let failCount = 0;
    for (const idx of queue) {
      if (animateAllAbortRef.current) break;
      markAnimating(idx, true);
      try {
        await animateScene(jobId, idx);
        await refreshApproval();
        const r = await waitForSceneAnimation(idx);
        if (r.ok) okCount += 1;
        else {
          failCount += 1;
          toast.error(`Scene ${idx + 1}: ${r.error}`);
        }
      } catch (err) {
        failCount += 1;
        toast.error(
          err?.response?.data?.detail || `Scene ${idx + 1} failed to start`,
        );
      } finally {
        markAnimating(idx, false);
      }
    }
    setAnimateAllBusy(false);
    if (okCount > 0) {
      toast.success(
        `Animated ${okCount}/${queue.length} scene${queue.length === 1 ? "" : "s"}`,
      );
    }
    if (failCount > 0 && okCount === 0) {
      toast.error(`All ${failCount} animation${failCount === 1 ? "" : "s"} failed`);
    }
  };

  const handleStopAnimateAll = () => {
    animateAllAbortRef.current = true;
    toast.info("Stopping after current scene finishes…");
  };

  const handleSaveAnimationPrompt = useCallback(
    async (idx, prompt) => {
      try {
        const r = await setSceneAnimationPrompt(jobId, idx, prompt || "");
        // Reflect the persisted value locally so the badge updates
        // without waiting for the next /scenes/approval-state poll.
        setSceneApproval((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          const arr = (next.scene_animation_prompts || []).slice();
          while (arr.length <= idx) arr.push("");
          arr[idx] = r?.prompt || "";
          next.scene_animation_prompts = arr;
          return next;
        });
      } catch (err) {
        toast.error(
          err?.response?.data?.detail || "Couldn't save animate prompt",
        );
      }
    },
    [jobId],
  );

  // Bulk-fill per-scene animate prompts from an uploaded .txt/.docx/.pdf.
  // We sequentially PATCH each scene so the server stays the source of
  // truth and downstream Animate calls pick up the correct text.
  const handleBulkAnimationPromptsFromDoc = useCallback(
    async (parsed) => {
      if (!jobId || !Array.isArray(parsed) || parsed.length === 0) return;
      const count = sceneApproval?.scenes_status?.length || 0;
      if (count === 0) {
        toast.error("Scenes not ready yet — try again in a moment");
        return;
      }
      const n = Math.min(parsed.length, count);
      let okN = 0;
      for (let i = 0; i < n; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await handleSaveAnimationPrompt(i, parsed[i] || "");
          okN += 1;
        } catch (err) {
          // Toast handled inside handleSaveAnimationPrompt.
        }
      }
      if (okN > 0) {
        toast.success(`Applied ${okN} scene prompt${okN === 1 ? "" : "s"}`);
      }
    },
    [jobId, sceneApproval, handleSaveAnimationPrompt],
  );

  // Concatenates every per-scene Veo3 clip + voiceover into the final
  // MP4. Polls until stitched_status flips to ready/failed.
  const [stitchBusy, setStitchBusy] = useState(false);
  const handleStitchFinalVideo = async () => {
    setStitchBusy(true);
    const tid = toast.loading("Stitching final video…");
    try {
      await stitchAnimatedScenes(jobId);
      await refreshApproval();
      // Poll up to 12 minutes (handles slow ffmpeg on heavy hosts).
      const deadline = Date.now() + 12 * 60 * 1000;
      let finalState = "running";
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        let snap;
        try {
          snap = await getSceneApprovalState(jobId);
        } catch {
          continue;
        }
        if (Array.isArray(snap?.scenes_status)) setSceneApproval(snap);
        const st = snap?.stitched_status || "";
        if (st === "ready" || st === "failed") {
          finalState = st;
          if (st === "failed") {
            toast.error(snap?.stitched_error || "Stitch failed", { id: tid });
          } else {
            toast.success("Final video ready", { id: tid });
          }
          break;
        }
      }
      if (finalState === "running") {
        toast.error("Stitch timed out — check back in a minute", { id: tid });
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't start stitch",
        { id: tid },
      );
    } finally {
      setStitchBusy(false);
    }
  };

  const restoreVersion = async (version) => {
    const confirmed = window.confirm(
      `Restore render version ${version} as the current video?\n\n`
      + "Your current video will be archived to history (and the oldest "
      + "history entry, if you have 3 already, will be dropped).",
    );
    if (!confirmed) return;
    setRestoringVersion(version);
    try {
      await restoreRenderVersion(jobId, version);
      toast.success(`Restored version ${version}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't restore version");
    } finally {
      setRestoringVersion(null);
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1200px] mx-auto" data-testid="auto-video-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3 flex items-center gap-3 flex-wrap">
        <span>// PHASE 3 · AUTO-VIDEO</span>
        {backendVersion?.pipeline_version && (
          (() => {
            const upToDate = backendVersion.pipeline_version === EXPECTED_PIPELINE_VERSION;
            return (
              <span
                data-testid="av-backend-version-badge"
                className={
                  "font-mono text-[10px] normal-case tracking-normal px-2 py-0.5 rounded-sm border " +
                  (upToDate
                    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                    : "border-red-400/60 bg-red-400/10 text-red-300 font-bold")
                }
                title={`backend pipeline: ${backendVersion.pipeline_version}`}
              >
                {upToDate
                  ? `✓ backend ${backendVersion.pipeline_version.split("-").slice(0, 4).join("-")}`
                  : `⚠ OLD BACKEND · ${backendVersion.pipeline_version} · CLICK FINISH UPDATE TO REDEPLOY`}
              </span>
            );
          })()
        )}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Your auto-generated video.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-6 leading-relaxed">
        Built from the Niche Playbook — channel kit, length-matched narration,
        and ElevenLabs voiceover in a single shot. Review below, then ship.
      </p>

      <RenderBudgetMeter />

      {loading && !doc && (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading<span className="blink">_</span>
        </div>
      )}

      {status === "running" && (
        <div className="border border-zinc-800 rounded-sm py-6 mb-8" data-testid="av-running">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-4 text-center">
            // BUILDING · STAGE: {STAGE_LABELS[stage] || stage || "—"}
          </div>
          {(() => {
            const fromActive = Boolean(doc?.source_active_channel_id);
            const stages = fromActive
              ? [
                  "Adapting playbook → niche profile",
                  "Writing narration script (length-matched)",
                  "Picking voice for channel style",
                  "Recording ElevenLabs voiceover",
                ]
              : [
                  "Adapting playbook → niche profile",
                  "Generating channel kit (Claude + image gen)",
                  "Writing narration script (length-matched)",
                  "Picking voice for channel style",
                  "Recording ElevenLabs voiceover",
                ];
            const logLines = fromActive
              ? [
                  "playbook → niche-shape adapter",
                  "claude-sonnet-4.5  · narration script · length match",
                  "voiceover_service.pick_voice() · style → voice_id",
                  "elevenlabs  · text-to-speech · eleven_multilingual_v2",
                  "→ writing mp3 to /uploads/voiceovers/",
                  "→ done · ready for review",
                ]
              : [
                  "playbook → niche-shape adapter",
                  "channel_kit_service.generate_channel_kit()",
                  "claude-sonnet-4.5  · channel names · taglines · about",
                  "claude-sonnet-4.5  · narration script · length match",
                  "gemini-2.5-flash-image  · banner_1 · banner_2 · avatar",
                  "voiceover_service.pick_voice() · style → voice_id",
                  "elevenlabs  · text-to-speech · eleven_multilingual_v2",
                  "→ writing mp3 to /uploads/voiceovers/",
                  "→ done · ready for review",
                ];
            return (
              <ScanLoader
                stages={stages}
                stageDurationMs={9000}
                subtitle={
                  fromActive
                    ? "~30–60s · script + voiceover (kit skipped — channel exists)"
                    : "~60–120s · running 3 services in parallel"
                }
                logLines={logLines}
              />
            );
          })()}
        </div>
      )}

      {status === "failed" && (
        <div className="border border-red-500/40 bg-red-500/5 p-6 rounded-sm mb-8" data-testid="av-failed">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-400 mb-2 font-bold">
            // PIPELINE FAILED
          </div>
          <p className="text-sm text-red-100">{doc?.error || "Unknown error"}</p>
        </div>
      )}

      {status === "complete" && result && (
        <div className="space-y-8" data-testid="av-result">
          {/* Channel header */}
          <div className="border border-zinc-800 rounded-sm p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
              // SOURCE CHANNEL
            </div>
            <div className="flex items-center gap-4">
              {channel?.thumbnail && (
                <img src={channel.thumbnail} alt={channel.name} className="w-12 h-12 rounded-full" />
              )}
              <div>
                <div className="text-white font-bold text-lg">{channel?.name}</div>
                <div className="font-mono text-xs text-zinc-400">
                  {nFmt(channel?.subscribers)} subs
                  {channel?.niche && <> · {channel.niche}</>}
                  {result.target_seconds && <> · target {dur(result.target_seconds)}</>}
                </div>
              </div>
            </div>
          </div>

          {/* Channel Kit */}
          {kit && (
            <section data-testid="av-kit">
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
                    // CHANNEL KIT
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">
                    Launch-ready brand assets
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="av-kit-regenerate-btn"
                    disabled={!!kitRegenerating}
                    onClick={() => handleRegenerateChannelKit()}
                    title="Roll the dice on new banner concepts — script + voiceover + video are NOT touched"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {kitRegenerating === "all" ? "↻ regenerating…" : "↻ regenerate all"}
                  </button>
                  <SaveKitButton
                    kit_key={jobId}
                    source_type="auto_video"
                    source_id={jobId}
                    channel_id={channel?.channel_id}
                    channel_name={channel?.name}
                    niche={channel?.niche}
                    name={kit?.channel_names?.[0]?.name || channel?.name || jobId}
                    data={kit}
                    images={kit?.images}
                  />
                </div>
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                {/* Banner + avatar preview — each image has its own
                    download button. Hover-overlay style so the UI stays
                    visually clean but the action is one click away. */}
                <div className="border border-zinc-800 rounded-sm p-4 bg-black space-y-4">
                  {["banner_1.png", "banner_2.png"].map((key, i) => (
                    kit.images?.[key] && (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400">
                            Banner {String.fromCharCode(65 + i)} · 2560×1440
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              data-testid={`av-kit-banner-${i + 1}-reroll-btn`}
                              disabled={!!kitRegenerating}
                              onClick={() => handleRegenerateChannelKit(key)}
                              title={`Re-generate only Banner ${String.fromCharCode(65 + i)} — keep the other one as-is`}
                              className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-2 py-1 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {kitRegenerating === key ? "↻ generating…" : "↻ re-generate"}
                            </button>
                            <button
                              type="button"
                              data-testid={`av-kit-banner-${i + 1}-download`}
                              onClick={() => downloadKitImage(
                                `${API_BASE}${kit.images[key]}`,
                                `banner_concept_${String.fromCharCode(65 + i)}.png`,
                              )}
                              className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-2 py-1 rounded-sm transition-colors"
                            >
                              ↓ download
                            </button>
                          </div>
                        </div>
                        <BannerSafeZonePreview
                          src={withCacheBust(
                            `${API_BASE}${kit.images[key]}`,
                            doc?.channel_kit_regenerated_at,
                          )}
                          alt={`Banner concept ${String.fromCharCode(65 + i)}`}
                        />
                        {(kit?.banner_history?.[key]?.length ?? 0) > 0 && (
                          <div
                            data-testid={`av-kit-banner-${i + 1}-history`}
                            className="flex items-center gap-2 flex-wrap pt-1"
                          >
                            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                              History · {kit.banner_history[key].length}
                            </span>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {[...kit.banner_history[key]]
                                .sort((a, b) => (b?.ts || 0) - (a?.ts || 0))
                                .map((h) => (
                                  <button
                                    key={h.filename}
                                    type="button"
                                    data-testid={`av-kit-banner-${i + 1}-restore-${h.filename}`}
                                    disabled={!!kitRegenerating}
                                    onClick={() => handleRestoreKitImage(key, h.filename)}
                                    title="Click to restore this version — current banner moves to history"
                                    className="relative group block border border-zinc-800 hover:border-yellow-400 rounded-sm overflow-hidden transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <img
                                      src={`${API_BASE}${h.url}`}
                                      alt={`Banner ${String.fromCharCode(65 + i)} version ${h.filename}`}
                                      className="block h-10 w-auto max-w-[160px] object-cover"
                                    />
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[8px] uppercase tracking-[0.2em] text-yellow-400 pointer-events-none">
                                      ↺ restore
                                    </span>
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                        <span data-testid={`av-kit-banner-${i + 1}`} className="hidden" />
                      </div>
                    )
                  ))}
                  <div className="flex items-center gap-3 pt-2 border-t border-zinc-900">
                    {kit.images?.["avatar.png"] && (
                      <div className="relative group">
                        <img
                          src={`${API_BASE}${kit.images["avatar.png"]}`}
                          alt="Avatar"
                          className="w-16 h-16 rounded-full border-2 border-zinc-700"
                          data-testid="av-kit-avatar"
                        />
                        <button
                          type="button"
                          data-testid="av-kit-avatar-download"
                          onClick={() => downloadKitImage(
                            `${API_BASE}${kit.images["avatar.png"]}`,
                            "avatar.png",
                          )}
                          title="Download avatar PNG"
                          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-yellow-400 text-black font-bold text-xs flex items-center justify-center hover:bg-yellow-300 transition-colors shadow-lg shadow-black/60"
                        >
                          ↓
                        </button>
                      </div>
                    )}
                    <div className="min-w-0">
                      {kit.channel_names?.[0] && (
                        <div className="text-white font-display text-xl font-bold truncate">
                          {kit.channel_names[0].name}
                        </div>
                      )}
                      {kit.channel_names?.[0]?.handle && (
                        <div className="font-mono text-xs text-zinc-400">
                          {kit.channel_names[0].handle}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Kit details */}
                <div className="border border-zinc-800 rounded-sm p-4 bg-black space-y-4">
                  {kit.channel_names?.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                          Name options
                        </div>
                        <div className="flex items-center gap-1.5">
                          {!kit.channel_names.some((n) => n?.availability) && (
                            <button
                              type="button"
                              data-testid="av-kit-check-names-btn"
                              onClick={() => handleCheckNames()}
                              disabled={checkingNames || refreshingNames}
                              className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-2 py-0.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {checkingNames ? "↻ checking…" : "↻ check availability"}
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid="av-kit-refresh-names-btn"
                            onClick={() => handleRefreshNames()}
                            disabled={checkingNames || refreshingNames}
                            title="Generate 5 brand-new name ideas. Excludes everything already shown."
                            className="font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-2 py-0.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {refreshingNames ? "↻ refreshing…" : "↻ refresh list"}
                          </button>
                        </div>
                      </div>
                      <ul className="space-y-1.5">
                        {kit.channel_names.slice(0, 5).map((n, i) => (
                          <li
                            key={n.name || i}
                            data-testid={`av-kit-name-option-${n.name || i}`}
                            className="flex items-baseline gap-2 flex-wrap text-xs text-zinc-200"
                          >
                            <span className="font-bold text-white">{n.name}</span>
                            <span className="font-mono text-zinc-500">{n.handle}</span>
                            <KitNameAvailabilityBadge availability={n.availability} />
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {kit.about_page && (
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-2">
                        About page
                      </div>
                      <p className="text-xs text-zinc-300 leading-relaxed">{kit.about_page}</p>
                    </div>
                  )}
                  {kit.color_palette && (
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-2">
                        Palette
                      </div>
                      <div className="flex gap-1.5">
                        {["primary", "secondary", "accent", "background"].map((k) =>
                          kit.color_palette[k] ? (
                            <div key={k} className="flex flex-col items-center">
                              <div
                                className="w-10 h-10 rounded-sm border border-zinc-700"
                                style={{ background: kit.color_palette[k] }}
                              />
                              <span className="font-mono text-[9px] text-zinc-500 mt-1">
                                {kit.color_palette[k]}
                              </span>
                            </div>
                          ) : null
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Idea + Script */}
          {script && (
            <section data-testid="av-script">
              <SectionHeader
                title="Your script"
                sub={`${nFmt((script.narration || "").split(/\s+/).length)} words · est ${dur(script.estimated_seconds)} · hook: ${script.hook_pattern_used || "—"}`}
              />
              <ScriptEditor
                jobId={jobId}
                doc={doc}
                onChanged={() => load()}
              />
            </section>
          )}

          {/* Voiceover */}
          {voiceover && (
            <section data-testid="av-voiceover">
              <SectionHeader title="Voiceover" sub={voiceover.voice_label} />
              <div className="border border-zinc-800 rounded-sm p-5 bg-black">
                <audio
                  // Force remount on each voiceover regen — see comment
                  // on the <video key=...> below for full rationale.
                  key={`vo-${doc?.script_edited_at || doc?.voiceover_regenerated_at || doc?.completed_at || ""}`}
                  data-testid="av-audio"
                  controls
                  preload="auto"
                  src={withCacheBust(
                    `${API_BASE}${voiceover.url}`,
                    doc?.script_edited_at || doc?.voiceover_regenerated_at || doc?.completed_at,
                  )}
                  className="w-full"
                />
                <div className="mt-3 flex items-center gap-3">
                  <a
                    href={withCacheBust(
                      `${API_BASE}${voiceover.url}`,
                      doc?.script_edited_at || doc?.voiceover_regenerated_at || doc?.completed_at,
                    )}
                    download={`${jobId}.mp3`}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 border border-yellow-400/40 hover:bg-yellow-400 hover:text-black px-3 py-2 rounded-sm transition-colors"
                  >
                    ↓ Download .mp3
                  </a>
                  <button
                    data-testid="av-vo-regen-btn"
                    onClick={triggerVoRegen}
                    disabled={regenVo}
                    title="Re-generate the MP3 via ElevenLabs (needed if the audio 404s after a redeploy)"
                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 disabled:text-zinc-500 px-3 py-2 rounded-sm transition-colors"
                  >
                    {regenVo ? "↻ Regenerating…" : "↻ Regenerate audio"}
                  </button>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    {(voiceover.size_bytes / 1024).toFixed(0)} KB · {voiceover.voice_id}
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* Thumbnail (matches the channel's visual signature) */}
          <section data-testid="av-thumbnail-section">
            <SectionHeader
              title="Click-worthy thumbnail"
              sub={
                thumbnail
                  ? `${(thumbnail.thumbnail_size_bytes / 1024).toFixed(0)} KB · ${thumbnail.provider}`
                  : "Generated using the channel's exact thumbnail-style rules"
              }
            />
            {!thumbnail ? (
              <div className="border border-zinc-800 rounded-sm p-6 bg-black text-center">
                <p className="text-sm text-zinc-300 mb-5 max-w-lg mx-auto leading-relaxed">
                  Render a 16:9 YouTube thumbnail that mimics this channel's
                  visual signature — color grade, composition, and framing
                  pulled straight from the Niche Playbook.
                </p>
                <button
                  data-testid="av-thumb-btn"
                  onClick={triggerThumbnail}
                  disabled={thumbing}
                  className="bg-yellow-400 text-black font-display text-base font-black uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-yellow-300 disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors shadow-[0_0_0_1px_rgba(250,204,21,0.5)]"
                >
                  {thumbing ? "▸ Generating…" : "▸ Generate thumbnail"}
                </button>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-3">
                  ~15–30s · single AI image
                </div>
              </div>
            ) : (
              <div className="border border-zinc-800 rounded-sm p-5 bg-black" data-testid="av-thumb-complete">
                <div className="relative aspect-video bg-zinc-900 rounded-sm overflow-hidden mb-4 max-w-2xl">
                  <img
                    // Force remount when thumbnail is regenerated
                    key={`thumb-${doc?.thumbnail_at || ""}`}
                    data-testid="av-thumb-img"
                    src={withCacheBust(
                      `${API_BASE}${thumbnail.thumbnail_url}`,
                      doc?.thumbnail_at,
                    )}
                    alt="Generated YouTube thumbnail"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <a
                    data-testid="av-thumb-download"
                    href={withCacheBust(
                      `${API_BASE}${thumbnail.thumbnail_url}`,
                      doc?.thumbnail_at,
                    )}
                    download={`${jobId}_thumbnail.png`}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-4 py-2 rounded-sm hover:bg-yellow-300 transition-colors"
                  >
                    ↓ Download .png
                  </a>
                  <button
                    data-testid="av-thumb-regen-btn"
                    onClick={triggerThumbnail}
                    disabled={thumbing}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 disabled:text-zinc-500 px-4 py-2 rounded-sm transition-colors"
                  >
                    {thumbing ? "↻ Generating…" : "↻ Regenerate"}
                  </button>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 ml-auto">
                    1280×720 · png
                  </span>
                </div>
                {thumbnail.style_rules_used?.length > 0 && (
                  <details className="border-t border-zinc-900 pt-4">
                    <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 cursor-pointer hover:text-yellow-300">
                      ▾ Style rules applied ({thumbnail.style_rules_used.length})
                    </summary>
                    <ul className="mt-3 space-y-1.5">
                      {thumbnail.style_rules_used.map((r, i) => (
                        <li key={i} className="flex gap-2 text-xs text-zinc-300">
                          <span className="text-yellow-400 font-mono shrink-0">▸</span>
                          <span className="leading-relaxed">{r}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}

                {/* User-guided regen panel: upload an example + comments */}
                <details
                  className="border-t border-zinc-900 pt-4 mt-2"
                  open={showThumbGuide}
                  onToggle={(e) => setShowThumbGuide(e.target.open)}
                  data-testid="av-thumb-guide"
                >
                  <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 cursor-pointer hover:text-yellow-300">
                    ▾ Guide the next regen — example image + notes
                  </summary>
                  <div className="mt-4 grid lg:grid-cols-[200px_1fr] gap-4">
                    {/* Reference upload */}
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
                        Reference thumbnail
                      </label>
                      {thumbRefPreview ? (
                        <div className="relative">
                          <img
                            src={thumbRefPreview}
                            alt="reference"
                            className="w-full aspect-video object-cover rounded-sm border border-zinc-800"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setThumbRefFile(null);
                              setThumbRefPreview(null);
                            }}
                            data-testid="av-thumb-ref-clear"
                            className="absolute top-1 right-1 font-mono text-[10px] uppercase tracking-[0.2em] bg-black/80 text-zinc-300 hover:text-red-400 px-2 py-1 rounded-sm border border-zinc-700"
                          >
                            ✕ Clear
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor="av-thumb-ref-input"
                          className="block w-full aspect-video border border-dashed border-zinc-700 hover:border-yellow-400/60 rounded-sm cursor-pointer flex items-center justify-center text-center px-3 transition-colors"
                        >
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                            ↑ Drop / pick an example
                          </span>
                        </label>
                      )}
                      <input
                        id="av-thumb-ref-input"
                        data-testid="av-thumb-ref-input"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={onPickThumbRef}
                        className="hidden"
                      />
                      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mt-2 leading-relaxed">
                        PNG/JPG · 10 MB max
                      </p>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
                        Comments / what you want
                      </label>
                      <textarea
                        data-testid="av-thumb-notes"
                        value={thumbNotes}
                        onChange={(e) => setThumbNotes(e.target.value)}
                        rows={5}
                        placeholder={"e.g. \u201CMake the dog the focal point. Use a yellow background. The expression should be shocked, mouth open. No food in the shot.\u201D"}
                        className="w-full bg-black border border-zinc-800 focus:border-yellow-400 rounded-sm px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors leading-relaxed resize-y"
                        maxLength={1200}
                      />
                      <div className="flex items-center justify-between mt-1">
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                          {thumbNotes.length}/1200 chars
                        </p>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                          Goes to Gemini as highest-priority instructions
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="av-thumb-regen-guided"
                        onClick={() => triggerThumbnail({
                          referenceFile: thumbRefFile,
                          notes: thumbNotes,
                        })}
                        disabled={thumbing || (!thumbRefFile && !thumbNotes.trim())}
                        className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-4 py-2 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {thumbing
                          ? "↻ Generating…"
                          : "★ Regenerate with my guidance"}
                      </button>
                    </div>
                  </div>
                </details>
              </div>
            )}
          </section>

          {/* Full Video (Phase 3B) */}
          <section data-testid="av-video-section">
            <SectionHeader
              title={sceneApproval ? "Scenes → animated video" : "Full MP4 video"}
              sub={
                video
                  ? `${(video.video_size_bytes / 1024 / 1024).toFixed(1)} MB · ${dur(video.duration_sec)} · ${video.scenes_count} AI scenes`
                  : videoStatus === "running"
                  ? VIDEO_STAGE_LABELS[videoStage] || "rendering…"
                  : sceneApproval
                    ? "Step 1 — approve every scene · Step 2 — animate"
                    : "AI scene images + ffmpeg Ken Burns assembly"
              }
            />

            {/* Active-Channels two-step pipeline (Scene approval flow).
                Shown INSTEAD of the legacy single-button render UI when
                this job has a scenes_status array. */}
            {!videoStatus && sceneApproval && (
              <div className="border border-zinc-800 rounded-sm p-5 bg-black space-y-5">
                <SceneApprovalList
                  state={sceneApproval}
                  apiBase={API_BASE}
                  onApprove={handleApproveScene}
                  onUnapprove={handleUnapproveScene}
                  onRegenerate={handleRegenerateScene}
                  onUpload={handleUploadSceneImage}
                  onAnimateAll={handleAnimateAll}
                  onRetryAll={handleRetryAllScenes}
                  animateBusy={animateBusy || rendering}
                  onAnimateScene={handleAnimateScene}
                  onAnimateAllApproved={handleAnimateAllApproved}
                  onStopAnimateAll={handleStopAnimateAll}
                  animatingScenes={animatingScenes}
                  animateAllBusy={animateAllBusy}
                  onStitchFinalVideo={handleStitchFinalVideo}
                  stitchBusy={stitchBusy}
                  onSaveAnimationPrompt={handleSaveAnimationPrompt}
                  onBulkAnimationPromptsFromDoc={handleBulkAnimationPromptsFromDoc}
                />
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 pt-3 border-t border-zinc-900">
                  Once every scene is ✓ approved, click <span className="text-emerald-400">▶ Animate all scenes</span> — each
                  scene becomes a short animated clip, then they&apos;re stitched together with your voiceover.
                </div>
              </div>
            )}

            {!videoStatus && !sceneApproval && (
              <div className="border border-zinc-800 rounded-sm p-6 bg-black text-center">
                <p className="text-sm text-zinc-300 mb-5 max-w-lg mx-auto leading-relaxed">
                  Generate AI images for each of the {script?.scene_breakdown?.length || 0}{" "}
                  scenes, animate them with the chosen image-to-video model, and
                  mux with your voiceover into a finished MP4 ready to upload.
                </p>
                <SceneCompositionCard
                  doc={doc}
                  jobId={jobId}
                  scenes={script?.scene_breakdown || []}
                  selectedProvider={selectedProvider}
                  videoProviders={videoProviders}
                  disabled={rendering}
                  onChanged={load}
                />
                <ImageProviderPicker
                  providers={imageProviders}
                  value={selectedImageProvider}
                  onChange={setSelectedImageProvider}
                  scenesCount={script?.scene_breakdown?.length || 6}
                />
                {(() => {
                  // Only show the AI video provider picker when at least
                  // one scene is set to "video" mode. For all-stills
                  // renders, the provider is irrelevant — local ffmpeg
                  // produces every clip and Veo/Kling are never called.
                  const docModes = Array.isArray(doc?.scene_modes) ? doc.scene_modes : [];
                  const sceneCount = script?.scene_breakdown?.length || 0;
                  const videoSceneCount = Array.from({ length: sceneCount }, (_, i) => {
                    const m = docModes[i];
                    return m === "video" ? 1 : 0;
                  }).reduce((a, b) => a + b, 0);
                  if (videoSceneCount === 0) {
                    return (
                      <div
                        className="border border-zinc-900 rounded-sm p-3 mb-4 text-left"
                        data-testid="av-provider-picker-disabled"
                      >
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                          🎬 AI video model · <span className="text-emerald-400">N/A</span>
                        </div>
                        <p className="text-xs text-zinc-400 mt-1.5 leading-snug">
                          All scenes are set to <span className="text-yellow-300">stills</span> — Veo / Kling won't be called.
                          Local ffmpeg will produce every clip for $0 in AI video cost.
                          Switch one or more scenes to <span className="text-yellow-300">Video</span> above to pick a provider.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <VideoProviderPicker
                      providers={videoProviders}
                      value={selectedProvider}
                      onChange={setSelectedProvider}
                      scenesCount={videoSceneCount}
                    />
                  );
                })()}
                <button
                  data-testid="av-render-btn"
                  onClick={triggerRender}
                  disabled={rendering}
                  className="bg-yellow-400 text-black font-display text-base font-black uppercase tracking-wide px-6 py-3 rounded-sm hover:bg-yellow-300 disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors shadow-[0_0_0_1px_rgba(250,204,21,0.5)] mt-2"
                >
                  {rendering ? "▸ Starting…" : "▸ Render full video"}
                </button>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-3">
                  ~30s–4min depending on selected model
                </div>
              </div>
            )}

            {videoStatus === "running" && (() => {
              const stageInfo = resolveVideoStage(videoStage);
              const info = STAGE_INFO[stageInfo.key] || { label: stageInfo.label, sub: "Working…" };
              const progressPct = stageInfo.progress
                ? Math.round((stageInfo.progress.done / stageInfo.progress.total) * 100)
                : null;
              // Per-stage typical durations (Kling clips take ~180s, LTX ~30s).
              const providerCfg = videoProviders.find((p) => p.id === (doc?.video_provider || selectedProvider));
              const perClipSec = providerCfg?.expected_seconds_per_call || 30;
              const totalScenes = script?.scene_breakdown?.length || 6;
              const stageEtaSec = ({
                queued: 10,
                regenerating_voiceover: 90,
                waiting_for_voiceover: 30,
                probing_audio: 5,
                scene_images: 30,
                ltx_video: perClipSec + 15,
                encoding_clips: 60,
                muxing: 10,
              })[stageInfo.key] || 30;
              const overallTotalSec = 10 + 30 + (perClipSec + 15) + 15;
              const totalElapsed = stageStartedAt
                ? Math.floor((Date.now() - stageStartedAt) / 1000)
                : 0;
              return (
              <div className="border border-zinc-800 rounded-sm bg-black" data-testid="av-video-running">
                {/* === USER-FRIENDLY HEADER === */}
                <div className="p-6 border-b border-zinc-900">
                  <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                    <div className="text-yellow-400 text-2xl">●</div>
                    <h2 className="text-xl text-white font-display font-bold leading-tight">
                      {info.label}
                    </h2>
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                    {info.sub}
                  </p>

                  {/* Per-step progress bar */}
                  {progressPct != null && (
                    <div className="mb-4" data-testid="av-step-progress">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="font-mono text-[11px] text-zinc-400">
                          {stageInfo.progress.done} of {stageInfo.progress.total} done
                        </span>
                        <span className="font-mono text-[11px] text-yellow-300">
                          {progressPct}%
                        </span>
                      </div>
                      <div className="h-2 bg-zinc-900 rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-yellow-400 transition-all duration-500"
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* === SCENE-BY-SCENE FOCUS STRIP ===
                      Shows during scene_images + ltx_video stages so the
                      user can see WHICH scene is currently being worked on
                      (not just "5 of 8 done"). Done scenes show ✓, active
                      scene pulses with a yellow ring + its visual description,
                      pending ones are dim. */}
                  {stageInfo.progress
                    && (stageInfo.key === "ltx_video" || stageInfo.key === "scene_images" || stageInfo.key === "encoding_clips")
                    && Array.isArray(script?.scene_breakdown)
                    && script.scene_breakdown.length > 0 && (() => {
                    const total = stageInfo.progress.total;
                    const activeIdx = Math.min(stageInfo.progress.done, total - 1); // 0-based, currently working
                    const scenes = script.scene_breakdown.slice(0, total);
                    const activeDesc = scenes[activeIdx] || "";
                    const animatingNow = stageInfo.key === "ltx_video" || stageInfo.key === "encoding_clips";
                    return (
                      <div className="mb-4" data-testid="av-scene-focus-strip">
                        {/* Active scene description callout */}
                        <div className="mb-3 px-3 py-2 border border-yellow-400/40 bg-yellow-400/5 rounded-sm">
                          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 mb-1 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" />
                            {animatingNow ? "Animating scene" : "Painting scene"} {activeIdx + 1} of {total}
                          </div>
                          <p
                            className="text-sm text-zinc-100 leading-snug"
                            data-testid="av-active-scene-desc"
                          >
                            {activeDesc || "—"}
                          </p>
                        </div>

                        {/* Horizontal thumbnail strip */}
                        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="av-scene-thumbs">
                          {scenes.map((desc, i) => {
                            const isDone = i < stageInfo.progress.done;
                            const isActive = i === activeIdx;
                            const sceneNum = String(i + 1).padStart(2, "0");
                            // Scene PNG exists once scene_images stage finishes that
                            // index. During ltx_video stage, all PNGs already exist.
                            // During scene_images stage, only done ones exist.
                            const hasImg =
                              animatingNow || isDone;
                            const imgUrl = hasImg
                              ? withCacheBust(
                                  `${API_BASE}/api/uploads/videos/${jobId}/scene_${sceneNum}.png`,
                                  doc?.video_started_at,
                                )
                              : null;
                            return (
                              <div
                                key={i}
                                title={desc}
                                data-testid={`av-scene-thumb-${i + 1}`}
                                data-active={isActive ? "true" : "false"}
                                data-done={isDone ? "true" : "false"}
                                className={
                                  "relative flex-shrink-0 w-20 aspect-video rounded-sm overflow-hidden border-2 transition-all " +
                                  (isActive
                                    ? "border-yellow-400 shadow-[0_0_0_3px_rgba(250,204,21,0.25)] scale-105"
                                    : isDone
                                    ? "border-emerald-500/40 opacity-90"
                                    : "border-zinc-800 opacity-40")
                                }
                              >
                                {imgUrl ? (
                                  <img
                                    src={imgUrl}
                                    alt={`Scene ${i + 1}`}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                  />
                                ) : (
                                  <div className="w-full h-full bg-zinc-900" />
                                )}
                                {/* Status overlay */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  {isDone ? (
                                    <span className="font-mono text-[10px] font-bold text-emerald-300 bg-black/70 px-1.5 py-0.5 rounded-sm">
                                      ✓
                                    </span>
                                  ) : isActive ? (
                                    <span className="font-mono text-[9px] font-bold text-yellow-300 bg-black/80 px-1.5 py-0.5 rounded-sm animate-pulse">
                                      ●
                                    </span>
                                  ) : null}
                                </div>
                                {/* Count-verification corner badge — appears
                                    as soon as a scene's verification result
                                    is written to the doc (mid-render). Lets
                                    the user spot "scene 7 got the count
                                    wrong" before the whole video finishes. */}
                                {(() => {
                                  const verif = (doc?.result?.count_verifications || [])
                                    .find((v) => v.scene_idx === i);
                                  if (!verif || verif.status === "unchecked") return null;
                                  const ok = verif.status === "verified";
                                  return (
                                    <span
                                      data-testid={`av-scene-thumb-${i + 1}-count`}
                                      title={
                                        ok
                                          ? `${verif.actual} ${verif.subject} ✓`
                                          : `got ${verif.actual}, wanted ${verif.expected}`
                                      }
                                      className={
                                        "absolute top-0.5 right-0.5 font-mono text-[9px] font-bold px-1 rounded-sm leading-none py-[2px] " +
                                        (ok
                                          ? "bg-emerald-500/90 text-black"
                                          : "bg-amber-500/90 text-black")
                                      }
                                    >
                                      {ok ? "✓" : "⚠"}
                                    </span>
                                  );
                                })()}
                                {/* Scene number badge */}
                                <div className="absolute bottom-0 left-0 right-0 bg-black/80 font-mono text-[9px] text-zinc-300 text-center py-0.5">
                                  {i + 1}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Stat strip: elapsed on this step + typical duration */}
                  <div className="grid grid-cols-3 gap-3 text-center mt-4">
                    <div className="border border-zinc-800 rounded-sm py-2 px-3">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                        Time on this step
                      </div>
                      <div className="font-mono text-base text-white">{totalElapsed}s</div>
                    </div>
                    <div className="border border-zinc-800 rounded-sm py-2 px-3">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                        Typical for this step
                      </div>
                      <div className={"font-mono text-base " + (totalElapsed > stageEtaSec * 1.5 ? "text-yellow-300" : "text-emerald-300")}>
                        ~{stageEtaSec}s
                      </div>
                    </div>
                    <div className="border border-zinc-800 rounded-sm py-2 px-3">
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                        Whole-render typical
                      </div>
                      <div className="font-mono text-base text-zinc-300">
                        ~{Math.ceil(overallTotalSec / 60)}min
                      </div>
                    </div>
                  </div>

                  {/* "Is this normal?" pill. Thresholds are intentionally
                      generous — fal's queue can legitimately make a stage
                      take 3-5x the ETA without anything actually being
                      wrong. We never tell the user to "consider cancelling"
                      because the Cancel Render button is already visible
                      below; the pill should reassure, not alarm. */}
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {(() => {
                      const greenCap = stageEtaSec * 3;
                      const yellowCap = stageEtaSec * 8;
                      if (totalElapsed < greenCap) {
                        return (
                          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-mono">
                            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                            Running normally · please wait
                          </span>
                        );
                      }
                      if (totalElapsed < yellowCap) {
                        return (
                          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 text-xs font-mono">
                            <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" />
                            GPU queue is busy · still working
                          </span>
                        );
                      }
                      return (
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/40 text-yellow-300 text-xs font-mono">
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-pulse" />
                          Taking longer than usual · still working
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* === ScanLoader (existing pretty animation) === */}
                <ScanLoader
                  stages={[
                    "Measuring your voiceover",
                    "Designing the scene images",
                    "Animating each scene",
                    "Stitching the final MP4",
                  ]}
                  stageKeys={[
                    "probing_audio",
                    "scene_images",
                    ["ltx_video", "encoding_clips"],
                    "muxing",
                  ]}
                  currentStage={stageInfo.key}
                  subtitle={info.sub}
                  logLines={[
                    "Reading your voiceover MP3",
                    "Asking AI to design each scene image",
                    "Each image takes about 5–10 seconds",
                    "Sending images into the video model",
                    "Each scene becomes a short motion clip",
                    "All scenes are rendered in parallel — not one by one",
                    "Joining all clips into one stream",
                    "Resizing to 720p widescreen",
                    "Mixing your voiceover audio over the video",
                    "Packaging as a YouTube-ready MP4",
                  ]}
                />
                {/* "Auto-recover" hint: surfaces only when the stage value
                    has not changed for >90 seconds — much smarter than the
                    old hard-coded "queued for 2 min" rule. Watchdog will
                    auto-fail the job at 120s anyway, so this is just a
                    user-facing reassurance. */}
                {/* Only show the "cancel + retry" prompt when this stage
                    has been stalled for a LONG time AND the user can't
                    see active progress. Kling clips legitimately take
                    60–180s each, so 90s on the same `ltx_video_N_of_6`
                    stage is normal — don't tempt the user to cancel a
                    healthy render. */}
                {stageStallSec > 180 && !["ltx_video", "muxing"].includes(stageInfo.key) && (
                  <div className="mt-4 mx-4 px-4 py-3 border border-yellow-400/40 bg-yellow-400/5 rounded-sm" data-testid="av-stage-stalled">
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 font-bold mb-1">
                      ⚠ This step has been running for {stageStallSec}s
                    </div>
                    <p className="text-xs text-zinc-300 mb-3">
                      AI servers may be busy right now. You can cancel this
                      render and try a different model below — or just wait,
                      it may finish on its own.
                    </p>
                    <div className="mb-3">
                      <VideoProviderPicker
                        providers={videoProviders}
                        value={selectedProvider}
                        onChange={setSelectedProvider}
                        scenesCount={script?.scene_breakdown?.length || 6}
                        compact
                      />
                    </div>
                    <button
                      data-testid="av-stalled-cancel-retry"
                      onClick={async () => {
                        try {
                          await cancelRender();
                          await new Promise((r) => setTimeout(r, 800));
                          await triggerRender({ force: true });
                        } catch {/* toasts already fired */}
                      }}
                      className="font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold hover:bg-yellow-300 px-4 py-2 rounded-sm transition-colors"
                    >
                      ✕ Cancel + retry with selected model
                    </button>
                  </div>
                )}
                {Array.isArray(doc?.image_log) && doc.image_log.length > 0 && (
                  <details className="mt-4 mx-4" data-testid="av-image-log-running">
                    <summary className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 cursor-pointer hover:text-yellow-400">
                      ▸ Show technical details ({doc.image_log.length} log lines)
                    </summary>
                    <div className="mt-2 max-h-48 overflow-auto bg-black/40 border border-zinc-800 rounded-sm p-3 font-mono text-[11px] text-zinc-400 space-y-0.5">
                      {(() => {
                        const visible = doc.image_log.slice(-20);
                        // For each clip/scene prefix, find the latest line.
                        // An "attempting…" line is "stale" if ANY later line
                        // (whether a newer attempt or a final OK/ERROR/FAILED)
                        // exists for the same prefix — meaning the original
                        // attempt has been superseded by progress.
                        const latestIdxByPrefix = new Map();
                        visible.forEach((line, i) => {
                          const m = line.match(/^(clip \d+|scene \d+):/);
                          if (m) latestIdxByPrefix.set(m[1], i);
                        });
                        return visible.map((line, i) => {
                          const m = line.match(/^(clip \d+|scene \d+):/);
                          const inFlight = line.endsWith("attempting…") || line.endsWith("attempting...");
                          const stale = inFlight && m && latestIdxByPrefix.get(m[1]) !== i;
                          return (
                            <LiveLogLine
                              key={`${i}-${line.slice(0, 24)}`}
                              line={line}
                              firstSeenMs={inFlightFirstSeen[line]}
                              isStale={stale}
                            />
                          );
                        });
                      })()}
                    </div>
                  </details>
                )}
                {stageInfo.key === "ltx_video" && (
                  <div className="mt-4 mx-4 px-4 py-3 border border-yellow-400/30 bg-yellow-400/5 rounded-sm" data-testid="av-ltx-info-banner">
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 font-bold mb-1">
                      ⏱ AI is creating your video clips
                    </div>
                    <p className="text-xs text-zinc-300">
                      Each scene clip takes roughly {perClipSec} seconds to generate.
                      All {stageInfo.progress?.total || totalScenes} clips run at the same time,
                      so total wait is about the same as one clip.
                      <span className="text-yellow-300"> Please don't cancel</span> — the AI is actively working.
                    </p>
                  </div>
                )}
                <div className="mt-4 text-center">
                  <button
                    data-testid="av-render-cancel-btn"
                    onClick={cancelRender}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 border border-zinc-800 hover:border-red-500 hover:text-red-400 px-4 py-1.5 rounded-sm transition-colors"
                  >
                    ✕ Cancel render
                  </button>
                </div>
              </div>
              );
            })()}

            {videoStatus === "failed" && (
              <div className="border border-red-500/40 bg-red-500/5 p-5 rounded-sm" data-testid="av-video-failed">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-red-400 mb-2 font-bold">
                  // RENDER FAILED
                </div>
                <p className="text-sm text-red-100 mb-3">{videoErr || "Unknown error"}</p>
                {Array.isArray(doc?.image_log) && doc.image_log.length > 0 && (
                  <details className="mb-3" data-testid="av-image-log">
                    <summary className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 cursor-pointer hover:text-yellow-400">
                      ▸ Image-gen diagnostic log ({doc.image_log.length} lines)
                    </summary>
                    <div className="mt-2 max-h-64 overflow-auto bg-black/40 border border-zinc-800 rounded-sm p-3 font-mono text-[11px] text-zinc-400 space-y-0.5">
                      {doc.image_log.map((line, i) => (
                        <div key={i} className={line.includes("TIMEOUT") || line.includes("ERROR") ? "text-red-300" : line.includes(" OK ") ? "text-emerald-300" : ""}>
                          {line}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                <div className="mb-3">
                  <VideoProviderPicker
                    providers={videoProviders}
                    value={selectedProvider}
                    onChange={setSelectedProvider}
                    scenesCount={script?.scene_breakdown?.length || 6}
                    compact
                  />
                </div>
                <button
                  data-testid="av-render-retry-btn"
                  onClick={() => triggerRender({ force: true })}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-yellow-400 border border-yellow-400/40 hover:bg-yellow-400 hover:text-black px-4 py-2 rounded-sm transition-colors"
                >
                  ↻ Retry render
                </button>
              </div>
            )}

            {videoStatus === "complete" && video && doc?.video_files_missing && (
              <div className="border border-yellow-400/40 bg-yellow-400/5 p-5 rounded-sm mb-4" data-testid="av-video-files-missing">
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-2 font-bold">
                  // VIDEO FILES CLEARED BY DEPLOY
                </div>
                <p className="text-sm text-yellow-100 mb-3">
                  This video was rendered before the most recent production deploy.
                  Server pods don't preserve generated MP4 files across deploys —
                  the metadata is still here, but the actual file is gone.
                  Click below to regenerate it (same script + voiceover, fresh AI scenes).
                </p>
                <button
                  data-testid="av-files-missing-rerender"
                  onClick={() => triggerRender({ force: true })}
                  className="font-mono text-xs uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold hover:bg-yellow-300 px-4 py-2 rounded-sm transition-colors"
                >
                  ↻ Regenerate video now
                </button>
              </div>
            )}

            {videoStatus === "complete" && video && !doc?.video_files_missing && (
              <div className="border border-zinc-800 rounded-sm p-5 bg-black" data-testid="av-video-complete">
                <video
                  // KEY forces React to UNMOUNT + REMOUNT the element on
                  // every fresh render, not just patch the src attribute.
                  // Without this, the browser's media element keeps the
                  // old MP4 in memory and ignores src changes — even with
                  // a cache-buster query param.
                  key={`mp4-${doc?.video_completed_at || ""}`}
                  data-testid="av-mp4-player"
                  controls
                  preload="metadata"
                  src={withCacheBust(
                    `${API_BASE}${video.video_url}`,
                    doc?.video_completed_at,
                  )}
                  poster={
                    thumbnail?.thumbnail_url && !doc?.thumbnail_files_missing
                      ? withCacheBust(
                          `${API_BASE}${thumbnail.thumbnail_url}`,
                          doc?.thumbnail_at,
                        )
                      : video.scene_images?.[0]
                      ? withCacheBust(
                          `${API_BASE}${video.scene_images[0]}`,
                          doc?.video_completed_at,
                        )
                      : undefined
                  }
                  className="w-full bg-black rounded-sm aspect-video"
                />
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <a
                    data-testid="av-mp4-download"
                    href={withCacheBust(
                      `${API_BASE}${video.video_url}`,
                      doc?.video_completed_at,
                    )}
                    download={`${jobId}.mp4`}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-4 py-2 rounded-sm hover:bg-yellow-300 transition-colors"
                  >
                    ↓ Download .mp4
                  </a>
                  <button
                    data-testid="av-mp4-rerender-btn"
                    onClick={() => triggerRender({ force: true })}
                    className="font-mono text-[11px] uppercase tracking-[0.2em] text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-2 rounded-sm transition-colors"
                  >
                    ↻ Re-render with new scenes
                  </button>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 ml-auto">
                    {(video.video_size_bytes / 1024 / 1024).toFixed(1)} MB · 720p H.264
                    {video.video_provider_label ? ` · ${video.video_provider_label}` : ""}
                  </span>
                </div>

                {lastRenderDiag ? (
                  <div
                    data-testid="render-diagnostics-panel"
                    className="mt-3 border border-yellow-400/30 bg-yellow-400/5 rounded-sm px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300 flex items-center gap-4 flex-wrap"
                  >
                    <span className="text-yellow-400 font-bold">↻ Cache wiped</span>
                    <span data-testid="diag-clips">
                      clips: <span className="text-white">{lastRenderDiag.invalidated.clips}</span>
                    </span>
                    <span data-testid="diag-scenes">
                      scenes: <span className="text-white">{lastRenderDiag.invalidated.scenes}</span>
                    </span>
                    <span data-testid="diag-work-files">
                      work files: <span className="text-white">{lastRenderDiag.invalidated.work_files}</span>
                    </span>
                    <span className="text-zinc-500 ml-auto normal-case tracking-normal">
                      {lastRenderDiag.provider} · {new Date(lastRenderDiag.at).toLocaleTimeString()}
                    </span>
                    <button
                      type="button"
                      onClick={() => setLastRenderDiag(null)}
                      className="text-zinc-500 hover:text-yellow-400 transition-colors"
                      data-testid="diag-dismiss-btn"
                      aria-label="Dismiss render diagnostics"
                    >
                      ✕
                    </button>
                  </div>
                ) : null}

                {/* Count-verification panel — one badge per scene with a
                    countable subject. Renders after the video preview so
                    users can spot at a glance which scenes the AI got
                    right (✓) vs which fell back after exhausting retries
                    (⚠). Hidden when no scenes have countable subjects. */}
                {Array.isArray(doc?.result?.count_verifications)
                  && doc.result.count_verifications.length > 0 ? (
                  <div
                    data-testid="count-verification-panel"
                    className="mt-3 border border-zinc-800 rounded-sm px-3 py-2"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 flex items-center gap-2">
                      <span>// Count check · {(() => {
                        const v = doc.result.count_verifications;
                        const ok = v.filter((x) => x.status === "verified").length;
                        return `${ok}/${v.length} verified`;
                      })()}</span>
                      <span
                        className="text-zinc-600 normal-case tracking-normal"
                        title="Gemini Vision counted each rendered scene and re-rolled the image up to 4 times if the count was wrong."
                      >
                        (?)
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {doc.result.count_verifications.map((v) => {
                        const ok = v.status === "verified";
                        const skipped = v.status === "unchecked";
                        const title = ok
                          ? `Scene ${v.scene_idx + 1}: ${v.actual} ${v.subject} — verified in ${v.attempts} attempt(s)`
                          : skipped
                          ? `Scene ${v.scene_idx + 1}: count check skipped (verifier unreachable)`
                          : `Scene ${v.scene_idx + 1}: got ${v.actual} ${v.subject}, wanted ${v.expected} — best of ${v.attempts} attempt(s)`;
                        return (
                          <span
                            key={v.scene_idx}
                            data-testid={`count-verif-${v.scene_idx}`}
                            data-status={v.status}
                            title={title}
                            className={
                              "font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border " +
                              (ok
                                ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10"
                                : skipped
                                ? "border-zinc-700 text-zinc-500 bg-zinc-900/40"
                                : "border-amber-500/50 text-amber-400 bg-amber-500/10")
                            }
                          >
                            {ok ? "✓" : skipped ? "·" : "⚠"} scene {v.scene_idx + 1} ·{" "}
                            {ok
                              ? `${v.actual} ${v.subject.split(" ").pop()}`
                              : skipped
                              ? "skipped"
                              : `${v.actual}/${v.expected} ${v.subject.split(" ").pop()}`}
                          </span>
                        );
                      })}
                    </div>
                    {doc.result.count_verifications.some((x) => x.status === "best_effort") ? (
                      <p
                        className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber-400/70 mt-2 normal-case tracking-normal"
                        data-testid="count-verif-hint"
                      >
                        ⚠ Some counts are off after max retries. Click ✎ Edit → Scene visual prompts to rephrase those scenes, or re-render to roll the dice again.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <SubjectInsertCard
                  jobId={jobId}
                  doc={doc}
                  onChanged={() => load()}
                />

                <div className="mt-4 pt-4 border-t border-zinc-900">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-2">
                    A/B test another model
                  </div>
                  <VideoProviderPicker
                    providers={videoProviders}
                    value={selectedProvider}
                    onChange={setSelectedProvider}
                    scenesCount={script?.scene_breakdown?.length || 6}
                    compact
                  />
                  <div className="mt-4 pt-3 border-t border-zinc-900">
                    <ImageProviderPicker
                      providers={imageProviders}
                      value={selectedImageProvider}
                      onChange={setSelectedImageProvider}
                      scenesCount={script?.scene_breakdown?.length || 6}
                    />
                  </div>
                </div>

                {doc?.render_history?.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-zinc-900" data-testid="av-render-history">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
                        ↻ Render history · {doc.render_history.length}
                      </div>
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                        Keeps last 3 versions
                      </span>
                    </div>
                    <div className="space-y-2">
                      {doc.render_history.map((h) => {
                        const restoring = restoringVersion === h.version;
                        const sizeMb = h.video_size_bytes
                          ? `${(h.video_size_bytes / 1024 / 1024).toFixed(1)} MB`
                          : null;
                        const completed = h.completed_at
                          ? new Date(h.completed_at)
                          : null;
                        return (
                          <div
                            key={h.version}
                            data-testid={`av-history-v${h.version}`}
                            className="flex items-center gap-3 px-3 py-2.5 border border-zinc-800 rounded-sm bg-zinc-950/60"
                          >
                            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 bg-yellow-400/10 border border-yellow-400/30 px-2 py-1 rounded-sm shrink-0">
                              v{h.version}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-[11px] text-zinc-300 truncate">
                                {h.video_provider_label || h.video_provider || "—"}
                              </div>
                              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5 flex flex-wrap gap-x-3">
                                {h.duration_sec ? <span>{Math.round(h.duration_sec)}s</span> : null}
                                {sizeMb ? <span>{sizeMb}</span> : null}
                                {typeof h.estimated_cost_usd === "number"
                                  ? <span>${h.estimated_cost_usd.toFixed(2)}</span>
                                  : null}
                                {completed
                                  ? <span>{completed.toLocaleString()}</span>
                                  : null}
                              </div>
                            </div>
                            <a
                              data-testid={`av-history-download-v${h.version}`}
                              href={withCacheBust(`${API_BASE}${h.video_url}`, h.completed_at)}
                              download={`${jobId}_v${h.version}.mp4`}
                              className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-2.5 py-1.5 rounded-sm transition-colors"
                              title="Download this version"
                            >
                              ↓
                            </a>
                            <button
                              type="button"
                              data-testid={`av-history-restore-v${h.version}`}
                              onClick={() => restoreVersion(h.version)}
                              disabled={restoringVersion !== null}
                              className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-wait"
                            >
                              {restoring ? "…" : "↩ Restore"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {video.scene_images?.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-zinc-900">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-3 flex items-center gap-3">
                      <span>Scenes used ({video.scene_images.length})</span>
                      {(() => {
                        const verifs = doc?.result?.count_verifications || [];
                        if (!verifs.length) return null;
                        const ok = verifs.filter((v) => v.status === "verified").length;
                        const bad = verifs.filter((v) => v.status === "best_effort").length;
                        return (
                          <span
                            data-testid="scenes-used-count-summary"
                            className="text-zinc-500 normal-case tracking-normal text-[10px]"
                          >
                            · count check:{" "}
                            <span className="text-emerald-400">{ok} ✓</span>
                            {bad > 0 ? (
                              <>
                                {" / "}
                                <span className="text-amber-400">{bad} ⚠</span>
                              </>
                            ) : null}
                          </span>
                        );
                      })()}
                    </div>
                    {/* Stale-render banner — appears as soon as any scene has
                        been user-replaced. The MP4 still has the old AI
                        image embedded, so we nudge the user to re-render. */}
                    {(doc?.result?.user_replaced_scenes || []).length > 0
                      && doc?.video_stale ? (
                      <div
                        data-testid="user-replaced-stale-banner"
                        className="mb-3 border border-amber-500/40 bg-amber-500/5 rounded-sm px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300 flex items-center gap-3 flex-wrap"
                      >
                        <span className="text-amber-400 font-bold">↑ {(doc.result.user_replaced_scenes || []).length} scene{(doc.result.user_replaced_scenes || []).length === 1 ? "" : "s"} replaced</span>
                        <span className="text-amber-300/80 normal-case tracking-normal">
                          Re-render to apply your uploads to the final MP4.
                        </span>
                      </div>
                    ) : null}
                    <input
                      ref={replaceInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      data-testid="scene-replace-file-input"
                      onChange={handleSceneFileChosen}
                    />
                    <div
                      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
                      data-testid="av-scene-thumbs"
                    >
                      {video.scene_images.map((src, i) => {
                        const verif = (doc?.result?.count_verifications || [])
                          .find((v) => v.scene_idx === i);
                        const isUserReplaced = (doc?.result?.user_replaced_scenes || []).includes(i);
                        const replacedAt = doc?.result?.user_replaced_at?.[i];
                        const isUploading = uploadingSceneIdx === i;
                        // Cache-bust on the user's replace timestamp when set
                        // (so the new image shows up immediately even though
                        // the URL is identical). Fall back to render
                        // completion timestamp for AI images.
                        const cacheBustToken = isUserReplaced
                          ? (replacedAt || doc?.video_completed_at)
                          : doc?.video_completed_at;
                        return (
                          <div
                            key={i}
                            data-testid={`scenes-used-${i}`}
                            data-user-replaced={isUserReplaced ? "true" : "false"}
                            className="relative aspect-video bg-zinc-900 rounded-sm overflow-hidden group"
                          >
                            <img
                              src={withCacheBust(`${API_BASE}${src}`, cacheBustToken)}
                              alt={`Scene ${i + 1}`}
                              className={
                                "w-full h-full object-cover transition-opacity " +
                                (isUploading ? "opacity-30" : "")
                              }
                            />
                            <div className="absolute top-1 left-1 font-mono text-[9px] uppercase tracking-[0.2em] text-yellow-400 bg-black/70 px-1.5 py-0.5 rounded-sm">
                              {String(i + 1).padStart(2, "0")}
                            </div>
                            {/* Corner status badge — priority: user-replaced
                                wins (since count-verify doesn't apply to
                                user images), else count-verify badge. */}
                            {isUserReplaced ? (
                              <div
                                data-testid={`scenes-used-${i}-user-badge`}
                                title={`Your upload · ${replacedAt ? new Date(replacedAt).toLocaleString() : ""}. Re-render to apply to MP4.`}
                                className="absolute top-1 right-1 font-mono text-[10px] font-bold px-1.5 py-[2px] leading-none rounded-sm bg-violet-500 text-black"
                              >
                                ↑ user
                              </div>
                            ) : verif && verif.status !== "unchecked" ? (
                              <div
                                data-testid={`scenes-used-${i}-count`}
                                data-status={verif.status}
                                title={
                                  verif.status === "verified"
                                    ? `Count verified — ${verif.actual} ${verif.subject} (${verif.attempts} attempt${verif.attempts === 1 ? "" : "s"})`
                                    : `Got ${verif.actual}, wanted ${verif.expected} — best of ${verif.attempts} attempts. Re-render to roll again.`
                                }
                                className={
                                  "absolute top-1 right-1 font-mono text-[10px] font-bold px-1.5 py-[2px] leading-none rounded-sm " +
                                  (verif.status === "verified"
                                    ? "bg-emerald-500 text-black"
                                    : "bg-amber-500 text-black")
                                }
                              >
                                {verif.status === "verified"
                                  ? `✓ ${verif.actual}`
                                  : `⚠ ${verif.actual}/${verif.expected}`}
                              </div>
                            ) : null}
                            {/* Hover overlay — Replace + Revert buttons.
                                Always reachable via keyboard tab order;
                                visual reveal kicks in on group-hover so
                                the grid stays clean. */}
                            <div
                              className={
                                "absolute inset-0 flex items-end justify-center gap-1.5 p-1.5 " +
                                "opacity-0 group-hover:opacity-100 focus-within:opacity-100 " +
                                "bg-gradient-to-t from-black/80 via-black/30 to-transparent " +
                                "transition-opacity pointer-events-none"
                              }
                            >
                              <button
                                type="button"
                                data-testid={`scenes-used-${i}-replace-btn`}
                                disabled={isUploading}
                                onClick={() => {
                                  pendingReplaceIdxRef.current = i;
                                  replaceInputRef.current?.click();
                                }}
                                className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-yellow-400/60 text-yellow-300 bg-black/60 hover:bg-yellow-400/20 pointer-events-auto disabled:opacity-40"
                              >
                                ↑ replace
                              </button>
                              {isUserReplaced ? (
                                <button
                                  type="button"
                                  data-testid={`scenes-used-${i}-revert-btn`}
                                  onClick={() => handleSceneRevert(i)}
                                  className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-zinc-600 text-zinc-300 bg-black/60 hover:border-red-400 hover:text-red-400 pointer-events-auto"
                                >
                                  ↶ revert
                                </button>
                              ) : null}
                              <button
                                type="button"
                                data-testid={`scenes-used-${i}-prompt-btn`}
                                onClick={() => setPromptEditorIdx(i)}
                                title="See & edit the AI prompt used to generate this scene image"
                                className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-zinc-600 text-zinc-300 bg-black/60 hover:border-yellow-400 hover:text-yellow-300 pointer-events-auto"
                              >
                                ✎ prompt
                              </button>
                              <a
                                href={withCacheBust(`${API_BASE}${src}`, cacheBustToken)}
                                download={`scene_${String(i + 1).padStart(2, "0")}.png`}
                                data-testid={`scenes-used-${i}-download-btn`}
                                title="Download this scene image (PNG)"
                                className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-zinc-600 text-zinc-300 bg-black/60 hover:border-yellow-400 hover:text-yellow-300 pointer-events-auto inline-flex items-center"
                                onClick={(e) => e.stopPropagation()}
                              >
                                ↓ save
                              </a>
                            </div>
                            {isUploading ? (
                              <div
                                className="absolute inset-0 flex items-center justify-center bg-black/50 pointer-events-none"
                                data-testid={`scenes-used-${i}-uploading`}
                              >
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300 animate-pulse">
                                  uploading…
                                </span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}
      {promptEditorIdx != null && (
        <ScenePromptEditor
          jobId={jobId}
          sceneIdx={promptEditorIdx}
          onClose={() => setPromptEditorIdx(null)}
          onSaved={() => { setPromptEditorIdx(null); load(); }}
        />
      )}
    </div>
  );
}

function ScenePromptEditor({ jobId, sceneIdx, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setLoadErr(null);
    getScenePrompt(jobId, sceneIdx)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setDraft(d.effective_prompt || d.default_prompt || "");
      })
      .catch((err) => alive && setLoadErr(err?.response?.data?.detail || err.message));
    return () => { alive = false; };
  }, [jobId, sceneIdx]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const toastId = toast.loading("Saving prompt…");
    try {
      await saveScenePrompt(jobId, sceneIdx, draft);
      toast.success("Prompt saved · re-render to apply", {
        id: toastId, duration: 4500,
      });
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed", { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (saving) return;
    setSaving(true);
    const toastId = toast.loading("Reverting to default prompt…");
    try {
      await resetScenePrompt(jobId, sceneIdx);
      toast.success("Override removed · using default again", {
        id: toastId, duration: 4500,
      });
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed", { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const isDirty = data && draft.trim() !== (data.effective_prompt || "").trim();

  return (
    <div
      data-testid="scene-prompt-modal"
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-zinc-800 shrink-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400">
            Scene {String(sceneIdx + 1).padStart(2, "0")} · AI prompt
            {data?.is_overridden && (
              <span className="ml-2 px-1.5 py-[1px] rounded-sm bg-violet-500 text-black text-[9px]">
                custom
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="scene-prompt-modal-close"
            className="font-mono text-xs text-zinc-500 hover:text-yellow-400 transition-colors"
          >
            close ✕
          </button>
        </header>
        <div className="px-5 py-4 overflow-auto space-y-4">
          {loadErr ? (
            <div className="font-mono text-xs text-red-400">{loadErr}</div>
          ) : !data ? (
            <div className="font-mono text-xs text-zinc-500 animate-pulse">
              Loading prompt…
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Scene description (from script)
                </div>
                <div className="text-sm text-zinc-200 bg-black border border-zinc-800 rounded-sm p-3 leading-relaxed">
                  {data.scene_text}
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    AI prompt (what gets sent to the image model)
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500">
                    {draft.length} / 8000
                  </div>
                </div>
                <textarea
                  data-testid="scene-prompt-textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={14}
                  className="w-full text-xs text-zinc-100 bg-black border border-zinc-800 rounded-sm p-3 font-mono leading-relaxed focus:border-yellow-400 focus:outline-none"
                />
                <div className="font-mono text-[10px] text-zinc-500 leading-relaxed pt-1">
                  Your prompt is sent VERBATIM to the image model — no boilerplate is appended.
                  Re-render the video to regenerate this scene with the new prompt.
                  {data.is_overridden && (
                    <>
                      {" "}Reset removes your override and falls back to the auto-built prompt.
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-800 shrink-0">
          {data?.is_overridden && (
            <button
              type="button"
              data-testid="scene-prompt-reset-btn"
              onClick={handleReset}
              disabled={saving}
              className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-red-400 hover:text-red-400 disabled:opacity-40"
            >
              ↶ reset to default
            </button>
          )}
          <button
            type="button"
            data-testid="scene-prompt-save-btn"
            onClick={handleSave}
            disabled={saving || !data || !isDirty || draft.trim().length < 10}
            className="font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-sm bg-yellow-400 text-black font-bold hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "saving…" : "save prompt"}
          </button>
        </footer>
      </div>
    </div>
  );
}


function VideoProviderPicker({ providers, value, onChange, scenesCount = 6, compact = false }) {
  if (!providers?.length) {
    return (
      <div className={"font-mono text-[11px] text-zinc-500 " + (compact ? "" : "mb-4")} data-testid="av-provider-picker-empty">
        Loading video providers…
      </div>
    );
  }
  const selected = providers.find((p) => p.id === value) || providers[0];
  const totalCost = (selected.cost_per_clip_usd * scenesCount).toFixed(2);
  const totalSec = selected.expected_seconds_per_call;

  return (
    <div className={compact ? "" : "mb-4 max-w-lg mx-auto"} data-testid="av-provider-picker">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-2 text-left">
        // VIDEO MODEL
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {providers.map((p) => {
          const active = p.id === value;
          const cost = (p.cost_per_clip_usd * scenesCount).toFixed(2);
          return (
            <button
              key={p.id}
              type="button"
              data-testid={`av-provider-${p.id}`}
              onClick={() => onChange(p.id)}
              className={
                "text-left px-3 py-2 rounded-sm border transition-colors " +
                (active
                  ? "border-yellow-400 bg-yellow-400/10"
                  : "border-zinc-800 hover:border-zinc-600 bg-black")
              }
            >
              <div className={"font-mono text-[12px] " + (active ? "text-yellow-300 font-bold" : "text-zinc-200")}>
                {p.label}
              </div>
              <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                ~${cost} for {scenesCount} clips · ~{p.expected_seconds_per_call}s/clip
              </div>
            </button>
          );
        })}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2 text-left">
        Selected: <span className="text-yellow-300">{selected.label}</span> · est. ${totalCost} total · ~{totalSec}s per clip
      </div>
    </div>
  );
}


// IMAGE PROVIDER PICKER — same UX shell as VideoProviderPicker but for
// scene-image generation. Three options: fal Flux (free-ish, fast),
// OpenAI gpt-image-1 (sharp, $$), Gemini Nano Banana Pro (text/faces, $$).
function ImageProviderPicker({ providers, value, onChange, scenesCount = 6 }) {
  if (!providers?.length) {
    return (
      <div className="font-mono text-[11px] text-zinc-500 mb-4" data-testid="av-image-picker-empty">
        Loading image providers…
      </div>
    );
  }
  const selected = providers.find((p) => p.id === value) || providers[0];
  const totalCost = (selected.cost_per_image_usd * scenesCount).toFixed(2);
  return (
    <div className="mb-4 max-w-lg mx-auto" data-testid="av-image-picker">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-2 text-left">
        // SCENE IMAGE MODEL
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        {providers.map((p) => {
          const active = p.id === value;
          const cost = (p.cost_per_image_usd * scenesCount).toFixed(2);
          return (
            <button
              key={p.id}
              type="button"
              data-testid={`av-image-provider-${p.id}`}
              onClick={() => onChange(p.id)}
              className={
                "text-left px-3 py-2 rounded-sm border transition-colors " +
                (active
                  ? "border-yellow-400 bg-yellow-400/10"
                  : "border-zinc-800 hover:border-zinc-600 bg-black")
              }
            >
              <div className={"font-mono text-[12px] " + (active ? "text-yellow-300 font-bold" : "text-zinc-200")}>
                {p.label}
              </div>
              <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                ~${cost} for {scenesCount} · ~{p.expected_seconds_per_image}s/img
              </div>
            </button>
          );
        })}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2 text-left">
        Selected: <span className="text-yellow-300">{selected.label}</span> · est. ${totalCost} total
      </div>
    </div>
  );
}

function SectionHeader({ title, sub }) {
  return (
    <div className="mb-3 flex items-baseline gap-3 flex-wrap">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
        // {title}
      </div>
      {sub && (
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          {sub}
        </div>
      )}
    </div>
  );
}
